import { useEffect, useState } from "react";
import type { UseMutationResult } from "react-query";
import type {
  PaywallFormRequest,
  PaywallResponse,
} from "@/react/api/paywallApi";
import { useIsPaywallCustomizationPhase2 } from "@/react/hooks/useIsPaywallCustomizationPhase2";
import type { Paywall } from "@/react/types";
import { useUpdateMemberAccessModal } from "../../UpdateMemberAccessModal";
import { tabs } from "../../constants";
import { usePaywallApi } from "../../hooks/usePaywallApi";
import type { PaywallForm } from "../../types/PaywallForm";
import { serializePaywallJson } from "../helpers/serializePaywallJson";
import { usePaywallFormDefaultValues } from "./usePaywallFormDefaultValues";

interface UsePaywallAdminFormModalProps {
  initialTab?: (typeof tabs)[keyof typeof tabs];
  isModalVisible: boolean;
  onClose: () => void;
  paywall?: Paywall;
  refetchPaywalls: () => void;
}

const advanceTabOrUpdate = (
  setActiveTab: (input: string) => void,
  activeTab: string,
  isPaywallCustomizationPhase2: boolean,
  mutation: UseMutationResult<Paywall, unknown, PaywallFormRequest>,
  data: PaywallForm,
) => {
  if (activeTab === tabs.DETAILS_TAB) {
    setActiveTab(tabs.PRICING_TAB);
  } else if (activeTab === tabs.PRICING_TAB) {
    setActiveTab(tabs.ACCESS_TAB);
  } else if (activeTab === tabs.ACCESS_TAB) {
    if (isPaywallCustomizationPhase2) {
      setActiveTab(tabs.CHECKOUT_TAB);
    } else {
      setActiveTab(tabs.POST_PURCHASE_TAB);
    }
  } else if (activeTab === tabs.CHECKOUT_TAB) {
    setActiveTab(tabs.POST_PURCHASE_TAB);
  } else if (activeTab === tabs.POST_PURCHASE_TAB) {
    setActiveTab(tabs.TRACKING_TAB);
  } else if (activeTab === tabs.TRACKING_TAB) {
    return mutation.mutate(serializePaywallJson(data));
  }
};

export const usePaywallAdminFormModal = ({
  paywall,
  refetchPaywalls,
  onClose,
  isModalVisible,
  initialTab = tabs.DETAILS_TAB,
}: UsePaywallAdminFormModalProps) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentPaywallStatus, setPaywallStatus] = useState<
    PaywallForm["status"] | undefined
  >(paywall?.status);
  const { show: showUpdateMemberAccessModal } = useUpdateMemberAccessModal();
  const { getDefaultValues } = usePaywallFormDefaultValues();

  useEffect(() => {
    if (!isModalVisible) {
      setActiveTab(initialTab);
    }
    setPaywallStatus(paywall?.status);
  }, [isModalVisible, initialTab, paywall?.status]);

  const createCallback = (paywallUpdated: PaywallResponse) => {
    onClose();
    refetchPaywalls();
    setPaywallStatus(paywallUpdated.status);
  };
  const updateCallback = (paywallUpdated: PaywallResponse) => {
    refetchPaywalls();
    setPaywallStatus(paywallUpdated.status);

    if (
      paywallUpdated.policies?.can_update_all_members_access &&
      paywallUpdated?.access_to_content_changed
    ) {
      void showUpdateMemberAccessModal({ paywallId: paywallUpdated.id });
    }
  };

  const { createMutation, updateMutation } = usePaywallApi({
    updateCallback,
    createCallback,
    paywall,
  });
  const mutation = paywall ? updateMutation : createMutation;
  const isPaywallCustomizationPhase2 = useIsPaywallCustomizationPhase2();

  const handleSubmit = (data: PaywallForm, { reset }: any): void => {
    const isEditingMode = !!data.id;
    if (isEditingMode) {
      updateMutation
        .mutateAsync(serializePaywallJson(data))
        .then(paywallResponse => {
          reset(getDefaultValues(paywallResponse));
        })
        .catch(err => {
          console.error(err);
        });
      return;
    }

    advanceTabOrUpdate(
      setActiveTab,
      activeTab,
      isPaywallCustomizationPhase2,
      createMutation,
      data,
    );
  };

  return {
    getDefaultValues,
    activeTab,
    setActiveTab,
    handleSubmit,
    mutation,
    currentPaywallStatus,
  };
};
